/**
 * The permission vocabulary, mirrored from `internal/auth/rbac.go`.
 *
 * ============================================================================
 * HIDING IS USER EXPERIENCE, NOT SECURITY.
 *
 * Every check in this file, every `meta.permission` on a route, and every
 * permission-filtered navigation item exists so that a site supervisor is not
 * shown a payout run wizard they cannot complete. NONE of it protects
 * anything. The server enforces the same matrix in middleware from each
 * OpenAPI operation's declared `x-permission`, and an authz matrix test
 * iterates every route against every role. A user who types the URL of a
 * screen they lack the permission for reaches it and then gets a 403 from the
 * API, which is the correct outcome: the boundary is the server's.
 *
 * Treating this list as a security control is the mistake this comment exists
 * to prevent.
 * ============================================================================
 */

export const PERMISSIONS = {
  masterdataRead: 'masterdata.read',
  masterdataWrite: 'masterdata.write',
  userManage: 'users.manage',
  roleManage: 'roles.manage',

  collectionRead: 'collection.read',
  collectionWrite: 'collection.write',
  collectionFinalize: 'collection.finalize',
  collectionManualEntry: 'collection.manual_entry',
  queueManage: 'queue.manage',

  deviceRead: 'devices.read',
  deviceManage: 'devices.manage',
  ingestReplay: 'ingest.replay',
  ingestResolve: 'ingest.resolve_conflict',

  sportsRead: 'sports.read',
  sportsWrite: 'sports.write',

  billingRead: 'billing.read',
  billingWrite: 'billing.write',
  billingFinalize: 'billing.finalize',
  billingVoid: 'billing.void',
  rateManage: 'rates.manage',
  walletRead: 'wallet.read',
  payoutRead: 'payouts.read',
  payoutRun: 'payouts.run',
  payoutConfirm: 'payouts.confirm',

  reportRead: 'reports.read',
  reportExport: 'reports.export',
  dashboardMD: 'dashboard.executive',
  auditRead: 'audit.read',

  integrationRead: 'integration.read',
  integrationManage: 'integration.manage',
  webhookManage: 'webhooks.manage',

  assistantUse: 'assistant.use',
  settingsManage: 'settings.manage',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/**
 * Human labels for the roles, used in the user menu and the audit browser's
 * actor column. The role IDS are persisted and must never be renamed
 * (CLAUDE.md conventions); only these labels are editorial.
 */
export const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Superadmin',
  md_executive: 'Managing Director',
  hq_ops: 'HQ operations',
  site_supervisor: 'Site supervisor',
  collector_operator: 'Collector operator',
  finance: 'Finance',
  auditor: 'Auditor',
  partner: 'Partner',
}

export function roleLabel(role: string | undefined): string {
  if (!role) return '—'
  return ROLE_LABELS[role] ?? role
}
